import { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  ClickAwayListener,
} from "@mui/material";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
  listOrderSets,
  expandOrderSet,
  createOrderSet,
  searchPharmacyItems,
  listInventoryUoms,
} from "../../api/orderSetsApi";

// ─── Local constants (small, presentation-only — deliberately kept separate
// from OrderManagementDrawer.js rather than exported out of a per-patient
// drawer component into this standalone admin page) ─────────────────────────

const cartFieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.78rem", py: "7px" },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
};

const PRESCRIPTION_FREQUENCY_OPTIONS = [
  "Daily",
  "Alternate Day",
  "Weekly",
  "Weekly Twice",
  "Weekly Thrice",
  "Fort Night",
  "Monthly",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

const MEDICINE_TYPE_OPTIONS = [
  "All",
  "Tablet",
  "Capsule",
  "Injection",
  "Syrup",
  "Inhaler",
  "Gel",
  "Drops",
  "Cream",
];
const DOSAGE_FORM_OPTIONS = MEDICINE_TYPE_OPTIONS.filter(
  (f) => f !== "All",
).map((label) => ({ label, value: label.toUpperCase() }));
const UOM_OPTIONS = [
  "Tablet",
  "Capsule",
  "ml",
  "mg",
  "g",
  "Vial",
  "Ampoule",
  "Unit",
  "Drop(s)",
  "Puff(s)",
  "Application",
  "Sachet",
];

const ITEM_SEARCH_LIMIT = 50;
const ITEM_SEARCH_DEBOUNCE_MS = 300;

const resolveSearchItemId = (item) => {
  const id = item?.item_id ?? item?.id ?? item?.inventory_item_id ?? null;
  if (id == null || id === "") return null;
  return String(id);
};

const resolveSearchItemName = (item) =>
  item?.item_name ||
  item?.name ||
  item?.brand_name ||
  item?.generic_name ||
  "Unnamed item";

const resolveItemUomFromSearch = (item) => {
  const uomId =
    item?.base_uom_id ??
    item?.uom_id ??
    item?.base_uom?.uom_id ??
    item?.base_uom?.id ??
    "";
  const uomLabel =
    item?.base_uom_name ??
    item?.uom_name ??
    item?.base_uom?.uom_name ??
    item?.base_uom?.name ??
    item?.uom_code ??
    "";
  return {
    uomId: uomId ? String(uomId) : "",
    uomLabel: uomLabel ? String(uomLabel) : "",
  };
};

const getSetId = (s) => s?.id ?? s?.order_set_id ?? s?.set_id;
const itemCountFor = (s) =>
  s?.item_count ?? (Array.isArray(s?.items) ? s.items.length : 0);

const drawerSelectMenuProps = {
  disableScrollLock: true,
  sx: { zIndex: 1500 },
  PaperProps: { sx: { zIndex: 1500, maxHeight: 280 } },
};

// ─── Small presentational atoms ──────────────────────────────────────────────

const FilterPill = ({ label, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 1.5,
      py: 0.5,
      borderRadius: "20px",
      fontSize: "0.75rem",
      fontWeight: 600,
      cursor: "pointer",
      color: active ? "#0E3F66" : "#6B7280",
      backgroundColor: active ? "#EFF6FF" : "#F9FAFB",
      border: `1px solid ${active ? "#BFDBFE" : "#E5E7EB"}`,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

const MonoBadge = ({ label }) => (
  <Box
    component="span"
    sx={{
      fontSize: "0.68rem",
      fontWeight: 700,
      color: "#6B7280",
      backgroundColor: "#F3F4F6",
      borderRadius: "4px",
      px: 0.7,
      py: 0.15,
      fontFamily: "monospace",
    }}
  >
    {label}
  </Box>
);

const SpecialtyPill = ({ label }) => (
  <Box
    component="span"
    sx={{
      fontSize: "0.68rem",
      fontWeight: 700,
      color: "#7C3AED",
      backgroundColor: "#F5F3FF",
      borderRadius: "10px",
      px: 0.9,
      py: 0.2,
    }}
  >
    {label}
  </Box>
);

const ColHeader = ({ children, width }) => (
  <TableCell
    sx={{
      width,
      fontSize: "0.72rem",
      fontWeight: 700,
      color: "#6B7280",
      textTransform: "uppercase",
      borderBottom: "1px solid #E5E7EB",
      py: 1,
    }}
  >
    {children}
  </TableCell>
);

const BodyCell = ({ children, sx }) => (
  <TableCell
    sx={{
      fontSize: "0.8rem",
      color: "#1F2937",
      borderBottom: "1px solid #F3F4F6",
      py: 1,
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

const LoadingBlock = ({ label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 5,
      color: "#6B7280",
    }}
  >
    <CircularProgress size={16} sx={{ color: "#0E3F66" }} />
    <Typography sx={{ fontSize: "0.82rem" }}>{label}</Typography>
  </Box>
);

const ErrorBlock = ({ message, onRetry }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      p: 1.5,
      my: 1,
      backgroundColor: "#FEF2F2",
      border: "1px solid #FECACA",
      borderRadius: 1.5,
    }}
  >
    <Typography sx={{ fontSize: "0.8rem", color: "#991B1B" }}>
      {message || "Something went wrong."}
    </Typography>
    <Button
      size="small"
      onClick={onRetry}
      sx={{
        fontSize: "0.76rem",
        fontWeight: 700,
        textTransform: "none",
        color: "#991B1B",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      Retry
    </Button>
  </Box>
);

// Read-only order-set row — no add-to-cart, since this page isn't patient-scoped.
const OrderSetSummaryRow = ({ set, state, onToggleExpand, onRetry }) => {
  const open = !!state?.open;
  const lines = state?.lines || [];
  const count = itemCountFor(set);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, mb: 1.5, overflow: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          p: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.86rem", color: "#1F2937" }}
            >
              {set.set_name}
            </Typography>
            <MonoBadge label={set.set_code} />
            {getSetId(set) && <MonoBadge label={`ID ${getSetId(set)}`} />}
            {set.specialty && <SpecialtyPill label={set.specialty} />}
          </Box>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#6B7280",
              mb: set.description ? 0.4 : 0,
            }}
          >
            {count} item{count === 1 ? "" : "s"}
          </Typography>
          {set.description && (
            <Typography
              sx={{
                fontSize: "0.76rem",
                color: "#9CA3AF",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {set.description}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onToggleExpand(set)}
          endIcon={
            open ? (
              <ExpandLessIcon sx={{ fontSize: 15 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 15 }} />
            )
          }
          sx={{
            fontSize: "0.74rem",
            textTransform: "none",
            py: 0.3,
            px: 1.2,
            minWidth: 0,
            color: "#374151",
            borderColor: "#E5E7EB",
            borderRadius: 1,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {open ? "Collapse" : "Expand"}
        </Button>
      </Box>

      {open && (
        <Box
          sx={{
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
            p: 1.5,
          }}
        >
          {state?.status === "loading" && (
            <LoadingBlock label="Loading items..." />
          )}
          {state?.status === "error" && (
            <ErrorBlock message={state.error} onRetry={() => onRetry(set)} />
          )}
          {state?.status === "loaded" &&
            (lines.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#9CA3AF",
                  textAlign: "center",
                  py: 2,
                }}
              >
                This order set has no items.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <ColHeader width="34%">Medicine</ColHeader>
                      <ColHeader width="22%">Frequency</ColHeader>
                      <ColHeader width="22%">Qty/Dose</ColHeader>
                      <ColHeader width="22%">Days</ColHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow
                        key={line.item_id}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <BodyCell sx={{ fontWeight: 600 }}>
                          {line.item_name}
                        </BodyCell>
                        <BodyCell
                          sx={{ fontSize: "0.78rem", color: "#374151" }}
                        >
                          {line.default_frequency_code || "—"}
                        </BodyCell>
                        <BodyCell
                          sx={{ fontSize: "0.78rem", color: "#374151" }}
                        >
                          {line.default_quantity_per_dose ?? "—"}
                        </BodyCell>
                        <BodyCell
                          sx={{ fontSize: "0.78rem", color: "#374151" }}
                        >
                          {line.default_days ?? "—"}
                        </BodyCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
        </Box>
      )}
    </Paper>
  );
};

// ─── Create form (transplanted from OrderManagementDrawer.js's CreateOrderSetForm) ──

const CreateOrderSetForm = ({ onCancel, onCreated }) => {
  const [form, setForm] = useState({
    set_code: "",
    set_name: "",
    specialty: "",
    description: "",
  });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [touched, setTouched] = useState(false);
  const seqRef = useRef(0);
  const searchInputRef = useRef(null);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [inventoryUoms, setInventoryUoms] = useState([]);

  useEffect(() => {
    listInventoryUoms()
      .then((rows) => setInventoryUoms(Array.isArray(rows) ? rows : []))
      .catch(() => setInventoryUoms([]));
  }, []);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const codeMissing = !form.set_code.trim();
  const nameMissing = !form.set_name.trim();
  const canSave = !codeMissing && !nameMissing;
  const codeError = touched && codeMissing ? "Set Code is required." : "";
  const nameError = touched && nameMissing ? "Set Name is required." : "";

  // Item search — debounced call to inventory/pharmacy/prescribe/search/.
  // Search text goes in `q` (brand/generic/name). Deliberately NOT scoped to a
  // pharmacy store: an order set is hospital-wide master data, so its items are
  // picked from the whole catalogue. Per-store availability is a question for the
  // moment the set is applied to a patient, and expandOrderSet already answers it
  // there (the Pharmacy tab shows its warnings against the selected store).
  const [medSearch, setMedSearch] = useState("");
  const [dosageForm, setDosageForm] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [medResults, setMedResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle"); // idle | loading | loaded | error
  const [searchError, setSearchError] = useState("");
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const searchSeqRef = useRef(0);
  const lastSearchRef = useRef({ offset: 0, append: false });

  const runSearch = (nextOffset, append) => {
    lastSearchRef.current = { offset: nextOffset, append };
    const seq = ++searchSeqRef.current;
    setSearchStatus("loading");
    setSearchError("");
    searchPharmacyItems({
      q: medSearch.trim() || undefined,
      dosageForm: dosageForm || undefined,
      availableOnly: availableOnly || undefined,
      limit: ITEM_SEARCH_LIMIT,
      offset: nextOffset,
    })
      .then(({ items: results, hasMore }) => {
        if (seq !== searchSeqRef.current) return; // a newer search superseded this one
        setMedResults((prev) => (append ? [...prev, ...results] : results));
        setHasMoreResults(hasMore);
        setSearchStatus("loaded");
      })
      .catch((err) => {
        if (seq !== searchSeqRef.current) return;
        setSearchError(err.message);
        setSearchStatus("error");
      });
  };

  const retrySearch = () =>
    runSearch(lastSearchRef.current.offset, lastSearchRef.current.append);

  useEffect(() => {
    if (!medSearch.trim() && !dosageForm && !availableOnly) {
      searchSeqRef.current += 1; // invalidate any in-flight request
      setMedResults([]);
      setHasMoreResults(false);
      setSearchStatus("idle");
      setSearchError("");
      return undefined;
    }
    const handle = setTimeout(
      () => runSearch(0, false),
      ITEM_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medSearch, dosageForm, availableOnly]);

  const addItemRow = (item) => {
    const itemId = resolveSearchItemId(item);
    const itemName = resolveSearchItemName(item);
    if (!itemId) {
      toast.error("Could not add this item — no item ID returned from search.");
      return;
    }
    if (items.some((it) => String(it.item_id) === itemId)) {
      toast.info(`${itemName} is already in this set.`);
      return;
    }
    const rawForm = (item.dosage_form || item.form || "").toLowerCase();
    const formTitleCase = rawForm
      ? rawForm.charAt(0).toUpperCase() + rawForm.slice(1)
      : "";
    const { uomId: searchUomId, uomLabel } = resolveItemUomFromSearch(item);
    const uomByName = inventoryUoms.find(
      (u) =>
        u.uom_name &&
        uomLabel &&
        u.uom_name.toLowerCase() === uomLabel.toLowerCase(),
    );
    const uomByForm = inventoryUoms.find(
      (u) =>
        u.uom_name &&
        formTitleCase &&
        u.uom_name.toLowerCase() === formTitleCase.toLowerCase(),
    );
    const resolvedUomId =
      searchUomId || uomByName?.uom_id || uomByForm?.uom_id || "";
    seqRef.current += 1;
    setItems((prev) => [
      ...prev,
      {
        tempId: `tmp-${itemId}-${seqRef.current}`,
        item_id: itemId,
        item_name: itemName,
        default_frequency_code:
          item.default_frequency_code || item.frequency_code || "Daily",
        default_quantity_per_dose: item.default_quantity_per_dose ?? 1,
        default_days: item.default_days ?? 5,
        uom_id: resolvedUomId,
        uom_label:
          uomLabel ||
          uomByName?.uom_name ||
          uomByForm?.uom_name ||
          formTitleCase ||
          "",
        sequence: prev.length,
      },
    ]);
    toast.success(
      `${itemName} added — search for another medicine to add more.`,
    );
    setSearchPanelOpen(false);
    setMedSearch("");
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const removeItemRow = (tempId) =>
    setItems((prev) =>
      prev
        .filter((it) => it.tempId !== tempId)
        .map((it, idx) => ({ ...it, sequence: idx })),
    );

  const updateItemRow = (tempId, field, value) =>
    setItems((prev) =>
      prev.map((it) => (it.tempId === tempId ? { ...it, [field]: value } : it)),
    );

  const uomSelectOptions = useMemo(() => {
    const byId = new Map();
    inventoryUoms.forEach((u) => {
      if (u.uom_id) byId.set(u.uom_id, u.uom_name || u.uom_id);
    });
    items.forEach((it) => {
      if (it.uom_id && !byId.has(it.uom_id)) {
        byId.set(it.uom_id, it.uom_label || it.uom_id);
      }
    });
    UOM_OPTIONS.forEach((name) => {
      if (!byId.has(name)) byId.set(name, name);
    });
    return Array.from(byId.entries()).map(([uom_id, uom_name]) => ({
      uom_id,
      uom_name,
    }));
  }, [inventoryUoms, items]);

  const handleSave = async () => {
    setTouched(true);
    if (!canSave) return;
    setSaving(true);
    setSaveError("");
    try {
      const created = await createOrderSet({
        set_code: form.set_code.trim(),
        set_name: form.set_name.trim(),
        specialty: form.specialty.trim() || null,
        description: form.description.trim() || null,
        items,
      });
      const newSet = {
        id: created?.id ?? created?.order_set_id ?? `local-${Date.now()}`,
        set_code: created?.set_code ?? form.set_code.trim(),
        set_name: created?.set_name ?? form.set_name.trim(),
        specialty: created?.specialty ?? (form.specialty.trim() || null),
        description: created?.description ?? (form.description.trim() || null),
        items: created?.items ?? items,
        item_count: created?.item_count ?? items.length,
      };
      toast.success("Order set created");
      onCreated(newSet);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        boxShadow: "0 1px 3px rgba(16,24,40,0.05)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          px: 3,
          py: 2.2,
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: "#FAFBFC",
        }}
      >
        <IconButton
          size="small"
          onClick={onCancel}
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            "&:hover": { backgroundColor: "#F3F4F6" },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 17, color: "#374151" }} />
        </IconButton>
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.96rem",
              color: "#111827",
              lineHeight: 1.3,
            }}
          >
            New Order Set
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#9CA3AF" }}>
            Create a reusable prescription template
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#9CA3AF",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            mb: 1.3,
          }}
        >
          Set Details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
            mb: 0.5,
          }}
        >
          <TextField
            size="small"
            label="Set Code"
            required
            value={form.set_code}
            onChange={(e) => setField("set_code", e.target.value)}
            onBlur={() => setTouched(true)}
            error={!!codeError}
            helperText={codeError || " "}
            inputProps={{ maxLength: 30 }}
            sx={cartFieldSx}
          />
          <TextField
            size="small"
            label="Set Name"
            required
            value={form.set_name}
            onChange={(e) => setField("set_name", e.target.value)}
            onBlur={() => setTouched(true)}
            error={!!nameError}
            helperText={nameError || " "}
            inputProps={{ maxLength: 120 }}
            sx={cartFieldSx}
          />
        </Box>
        <TextField
          size="small"
          fullWidth
          label="Specialty (optional)"
          value={form.specialty}
          onChange={(e) => setField("specialty", e.target.value)}
          sx={{ mb: 1.5, ...cartFieldSx }}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          rows={2}
          label="Description (optional)"
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          sx={{ mb: 1, ...cartFieldSx }}
        />

        <Box sx={{ borderTop: "1px dashed #E5E7EB", my: 2.5 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Items
            </Typography>
            {items.length > 0 && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "#0E3F66",
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  borderRadius: "10px",
                  px: 0.9,
                  py: 0.1,
                }}
              >
                {items.length}
              </Box>
            )}
          </Box>
          {items.length > 0 && (
            <Typography sx={{ fontSize: "0.72rem", color: "#9CA3AF" }}>
              Search above to add more medicines
            </Typography>
          )}
        </Box>
        <ClickAwayListener onClickAway={() => setSearchPanelOpen(false)}>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              inputRef={searchInputRef}
              placeholder="Search medicine to add to this set..."
              value={medSearch}
              onFocus={() => setSearchPanelOpen(true)}
              onChange={(e) => {
                setSearchPanelOpen(true);
                setMedSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 0.6,
                mt: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <FilterPill
                label="All Forms"
                active={!dosageForm}
                onClick={() => {
                  setDosageForm("");
                  setSearchPanelOpen(true);
                }}
              />
              {DOSAGE_FORM_OPTIONS.map((opt) => (
                <FilterPill
                  key={opt.value}
                  label={opt.label}
                  active={dosageForm === opt.value}
                  onClick={() => {
                    setDosageForm((cur) =>
                      cur === opt.value ? "" : opt.value,
                    );
                    setSearchPanelOpen(true);
                  }}
                />
              ))}
              <FilterPill
                label="In Stock Only"
                active={availableOnly}
                onClick={() => {
                  setAvailableOnly((v) => !v);
                  setSearchPanelOpen(true);
                }}
              />
            </Box>

            {searchPanelOpen && searchStatus !== "idle" && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 0.5,
                  borderRadius: 2,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {medResults.map((item, idx) => {
                  const itemId = resolveSearchItemId(item);
                  const itemName = resolveSearchItemName(item);
                  const meta = [
                    item.generic_name || item.generic,
                    item.brand_name || item.brand,
                    item.dosage_form || item.form,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const added = itemId
                    ? items.some((it) => String(it.item_id) === itemId)
                    : false;
                  return (
                    <Box
                      key={itemId || `search-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 1,
                        borderBottom: "1px solid #F3F4F6",
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#1F2937",
                          }}
                        >
                          {itemName}
                        </Typography>
                        {meta && (
                          <Typography
                            sx={{ fontSize: "0.68rem", color: "#9CA3AF" }}
                          >
                            {meta}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        startIcon={
                          added ? null : <AddIcon sx={{ fontSize: 13 }} />
                        }
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addItemRow(item)}
                        disabled={added || !itemId}
                        sx={{
                          fontSize: "0.72rem",
                          textTransform: "none",
                          py: 0.2,
                          px: 1,
                          minWidth: 0,
                          flexShrink: 0,
                          color: added ? "#21cdc0" : "#0E3F66",
                          border: `1px solid ${added ? "#21cdc0" : "#BFDBFE"}`,
                          borderRadius: 1,
                          backgroundColor: added ? "#f0fffe" : "#EFF6FF",
                          "&:hover": {
                            backgroundColor: added ? "#f0fffe" : "#DBEAFE",
                          },
                        }}
                      >
                        {added ? "Added" : "Add"}
                      </Button>
                    </Box>
                  );
                })}

                {searchStatus === "loading" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1.2,
                    }}
                  >
                    <CircularProgress size={13} sx={{ color: "#0E3F66" }} />
                    <Typography sx={{ fontSize: "0.76rem", color: "#6B7280" }}>
                      Searching...
                    </Typography>
                  </Box>
                )}

                {searchStatus === "error" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.5,
                      py: 1.2,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.76rem", color: "#991B1B" }}>
                      {searchError || "Search failed."}
                    </Typography>
                    <Button
                      size="small"
                      onClick={retrySearch}
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "none",
                        color: "#991B1B",
                        minWidth: 0,
                        flexShrink: 0,
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                )}

                {searchStatus === "loaded" && medResults.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#9CA3AF",
                      textAlign: "center",
                      py: 1.5,
                    }}
                  >
                    No matches found.
                  </Typography>
                )}

                {hasMoreResults &&
                  searchStatus !== "loading" &&
                  medResults.length > 0 && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 0.8,
                        borderTop: "1px solid #F3F4F6",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => runSearch(medResults.length, true)}
                        sx={{
                          fontSize: "0.74rem",
                          textTransform: "none",
                          color: "#0E3F66",
                          fontWeight: 600,
                          minWidth: 0,
                        }}
                      >
                        Load more
                      </Button>
                    </Box>
                  )}
              </Paper>
            )}
          </Box>
        </ClickAwayListener>

        <Box>
          {items.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                py: 4,
                px: 2,
                textAlign: "center",
                backgroundColor: "#FAFAFA",
                borderStyle: "dashed",
              }}
            >
              <ListAltIcon sx={{ fontSize: 26, color: "#D1D5DB", mb: 0.6 }} />
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.8rem" }}>
                No items added yet. Search above to add medicines to this set.
              </Typography>
            </Paper>
          ) : (
            items.map((it) => (
              <Paper
                key={it.tempId}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1,
                  "&:last-child": { mb: 0.5 },
                  transition: "box-shadow .15s, border-color .15s",
                  "&:hover": {
                    boxShadow: "0 1px 4px rgba(16,24,40,0.08)",
                    borderColor: "#D1D5DB",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    mb: 1.2,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: "#0E3F66",
                        color: "#fff",
                        fontSize: "0.66rem",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.15,
                      }}
                    >
                      {it.sequence + 1}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.84rem",
                          color: "#1F2937",
                          lineHeight: 1.3,
                        }}
                      >
                        {it.item_name}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeItemRow(it.tempId)}
                    sx={{
                      color: "#9CA3AF",
                      p: 0.4,
                      mt: -0.3,
                      "&:hover": {
                        color: "#EF4444",
                        backgroundColor: "#FEF2F2",
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1,
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Default Frequency"
                    value={it.default_frequency_code}
                    onChange={(e) =>
                      updateItemRow(
                        it.tempId,
                        "default_frequency_code",
                        e.target.value,
                      )
                    }
                    SelectProps={{ native: true }}
                    sx={cartFieldSx}
                  >
                    {PRESCRIPTION_FREQUENCY_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="UOM"
                    value={it.uom_id || ""}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      const match = uomSelectOptions.find(
                        (u) => u.uom_id === nextId,
                      );
                      setItems((prev) =>
                        prev.map((row) =>
                          row.tempId === it.tempId
                            ? {
                                ...row,
                                uom_id: nextId,
                                uom_label: match?.uom_name || row.uom_label,
                              }
                            : row,
                        ),
                      );
                    }}
                    SelectProps={{ native: true }}
                    helperText={
                      it.uom_id ? "" : "Select unit from inventory master"
                    }
                    sx={cartFieldSx}
                  >
                    <option value="">Select unit</option>
                    {uomSelectOptions.map((u) => (
                      <option key={u.uom_id} value={u.uom_id}>
                        {u.uom_name}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qty / Dose"
                    value={it.default_quantity_per_dose}
                    onChange={(e) =>
                      updateItemRow(
                        it.tempId,
                        "default_quantity_per_dose",
                        e.target.value === ""
                          ? ""
                          : Math.max(0, parseFloat(e.target.value) || 0),
                      )
                    }
                    inputProps={{ min: 0 }}
                    sx={cartFieldSx}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Days"
                    value={it.default_days}
                    onChange={(e) =>
                      updateItemRow(
                        it.tempId,
                        "default_days",
                        e.target.value === ""
                          ? ""
                          : Math.max(0, parseInt(e.target.value, 10) || 0),
                      )
                    }
                    inputProps={{ min: 0 }}
                    sx={cartFieldSx}
                  />
                </Box>
              </Paper>
            ))
          )}
        </Box>

        {saveError && (
          <Box
            sx={{
              p: 1.5,
              mt: 1.5,
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 1.5,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: "#991B1B" }}>
              {saveError}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "#FAFBFC",
        }}
      >
        {!canSave && (
          <Typography
            sx={{
              fontSize: "0.76rem",
              color: touched ? "#B91C1C" : "#9CA3AF",
              mr: "auto",
            }}
          >
            Set Code and Set Name are required to save.
          </Typography>
        )}
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            fontSize: "0.82rem",
            textTransform: "none",
            borderColor: "#D1D5DB",
            color: "#374151",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !canSave}
          sx={{
            fontSize: "0.82rem",
            textTransform: "none",
            backgroundColor: "#0E3F66",
            "&:hover": { backgroundColor: "#0a2e4d" },
            "&:disabled": { backgroundColor: "#93A5B8", color: "#fff" },
          }}
        >
          {saving ? "Saving..." : "Save Order Set"}
        </Button>
      </Box>
    </Paper>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────

const OrderSetsAdminPanel = () => {
  const [view, setView] = useState("browse");
  const [orderSets, setOrderSets] = useState([]);
  const [listStatus, setListStatus] = useState("loading");
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [expandState, setExpandState] = useState({});
  const expandCacheRef = useRef(new Map());

  const loadOrderSets = () => {
    setListStatus("loading");
    setListError("");
    listOrderSets()
      .then((data) => {
        setOrderSets(Array.isArray(data) ? data : []);
        setListStatus("loaded");
      })
      .catch((err) => {
        setListError(err.message);
        setListStatus("error");
      });
  };

  useEffect(() => {
    loadOrderSets();
  }, []);

  const specialties = useMemo(() => {
    const set = new Set();
    orderSets.forEach((s) => {
      if (s.specialty) set.add(s.specialty);
    });
    return Array.from(set).sort();
  }, [orderSets]);

  const filteredSets = useMemo(() => {
    let list = orderSets;
    if (specialty) list = list.filter((s) => s.specialty === specialty);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          (s.set_name || "").toLowerCase().includes(q) ||
          (s.set_code || "").toLowerCase().includes(q) ||
          (s.specialty || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [orderSets, specialty, search]);

  const fetchExpand = (set) => {
    const id = getSetId(set);
    if (expandCacheRef.current.has(id)) {
      const lines = expandCacheRef.current.get(id);
      setExpandState((prev) => ({
        ...prev,
        [id]: { ...prev[id], open: true, status: "loaded", lines, error: "" },
      }));
      return;
    }
    setExpandState((prev) => ({
      ...prev,
      [id]: { ...prev[id], open: true, status: "loading", error: "" },
    }));
    expandOrderSet(id)
      .then((lines) => {
        const safeLines = Array.isArray(lines) ? lines : [];
        expandCacheRef.current.set(id, safeLines);
        setExpandState((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            open: true,
            status: "loaded",
            lines: safeLines,
            error: "",
          },
        }));
      })
      .catch((err) => {
        setExpandState((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            open: true,
            status: "error",
            error: err.message,
          },
        }));
      });
  };

  const handleToggleExpand = (set) => {
    const id = getSetId(set);
    if (expandState[id]?.open) {
      setExpandState((prev) => ({
        ...prev,
        [id]: { ...prev[id], open: false },
      }));
      return;
    }
    fetchExpand(set);
  };

  const handleSetCreated = () => {
    setView("browse");
    loadOrderSets();
  };

  if (view === "create") {
    return (
      <Box sx={{ p: 3, maxWidth: 1000 }}>
        <CreateOrderSetForm
          onCancel={() => setView("browse")}
          onCreated={handleSetCreated}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography
        sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#121959", mb: 0.3 }}
      >
        Order Sets
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "#6B7280", mb: 2.5 }}>
        Reusable prescription templates any doctor can apply to a patient from
        Order Management.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by set name, code, or specialty..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.85rem" } }}
        />
        <Button
          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          variant="contained"
          onClick={() => setView("create")}
          sx={{
            fontSize: "0.78rem",
            textTransform: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            backgroundColor: "#0E3F66",
            "&:hover": { backgroundColor: "#0a2e4d" },
          }}
        >
          New Order Set
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 0.8,
          mb: 2.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FilterPill
          label="All Sets"
          active={!specialty}
          onClick={() => setSpecialty("")}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            displayEmpty
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            MenuProps={drawerSelectMenuProps}
            renderValue={(v) =>
              v || (
                <Typography sx={{ color: "#6B7280", fontSize: "0.75rem" }}>
                  By Specialty
                </Typography>
              )
            }
            sx={{
              fontSize: "0.75rem",
              borderRadius: "20px",
              backgroundColor: specialty ? "#EFF6FF" : "#F9FAFB",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: specialty ? "#BFDBFE" : "#E5E7EB",
              },
              "& .MuiSelect-select": { py: "6px", px: 1.5 },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.8rem" }}>
              All Specialties
            </MenuItem>
            {specialties.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {listStatus === "loading" && (
        <LoadingBlock label="Loading order sets..." />
      )}
      {listStatus === "error" && (
        <ErrorBlock message={listError} onRetry={loadOrderSets} />
      )}

      {listStatus === "loaded" && orderSets.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 1.5,
          }}
        >
          <ListAltIcon sx={{ fontSize: 48, color: "#E5E7EB" }} />
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#374151" }}
          >
            No order sets yet for this hospital.
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#9CA3AF" }}>
            Create your first order set to speed up common prescriptions.
          </Typography>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            variant="contained"
            onClick={() => setView("create")}
            sx={{
              fontSize: "0.78rem",
              textTransform: "none",
              backgroundColor: "#0E3F66",
              "&:hover": { backgroundColor: "#0a2e4d" },
              mt: 1,
            }}
          >
            New Order Set
          </Button>
        </Box>
      )}

      {listStatus === "loaded" &&
        orderSets.length > 0 &&
        filteredSets.length === 0 && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <ListAltIcon sx={{ fontSize: 48, color: "#E5E7EB", mb: 1.5 }} />
            <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
              No order sets match your filters.
            </Typography>
          </Box>
        )}

      {listStatus === "loaded" &&
        filteredSets.map((set) => (
          <OrderSetSummaryRow
            key={getSetId(set)}
            set={set}
            state={expandState[getSetId(set)]}
            onToggleExpand={handleToggleExpand}
            onRetry={fetchExpand}
          />
        ))}
    </Box>
  );
};

export default OrderSetsAdminPanel;
